import React, { useState } from "react";
import { useAuth } from "../context/AuthContext";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { api, formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { User, Mail, CreditCard, Shield, Activity } from "lucide-react";
import SupportCard from "../components/SupportCard";

export default function Profile() {
  const { user, setUser } = useAuth();
  const [name, setName] = useState(user?.name || "");
  const [busy, setBusy] = useState(false);

  if (!user) return null;

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.patch("/api/auth/me", { name });
      setUser(data);
      toast.success("Profile updated");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to update");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex flex-col" data-testid="profile-page">
      <Header />
      <main id="main-content" className="flex-1 max-w-[1000px] w-full mx-auto px-4 sm:px-6 py-12">
        <div className="label-mono">Account</div>
        <h1 className="text-4xl text-white font-semibold tracking-tight mt-1">Profile</h1>

        <div className="grid md:grid-cols-3 gap-5 mt-10">
          <div className="md:col-span-2 card-surface p-6">
            <div className="flex items-center gap-2.5 mb-5">
              <div className="w-9 h-9 rounded-xl bg-white/[0.06] border border-white/10 flex items-center justify-center">
                <User className="w-4 h-4 text-white" />
              </div>
              <div>
                <div className="label-mono">Personal</div>
                <h3 className="text-white font-semibold tracking-tight text-[15px]">Profile info</h3>
              </div>
            </div>
            <div className="space-y-4">
              <div>
                <label className="label-mono mb-1.5 block">Name</label>
                <input value={name} onChange={(e) => setName(e.target.value)} className="input-glass" data-testid="profile-name-input" />
              </div>
              <div>
                <label className="label-mono mb-1.5 block">Email</label>
                <div className="flex items-center gap-2 px-3 py-2.5 border border-white/8 rounded-lg text-sm font-mono text-white/60 bg-white/[0.02]">
                  <Mail className="w-3.5 h-3.5" />
                  <span data-testid="profile-email">{user.email}</span>
                </div>
              </div>
              <div>
                <label className="label-mono mb-1.5 block">Auth provider</label>
                <div className="px-3 py-2.5 border border-white/8 rounded-lg text-sm font-mono text-white/60 uppercase tracking-wider bg-white/[0.02]" data-testid="profile-provider">
                  {user.auth_provider}
                </div>
              </div>
              <button onClick={save} disabled={busy} className="btn-primary mt-1" data-testid="profile-save-btn">
                {busy ? "Saving…" : "Save changes"}
              </button>
            </div>
          </div>
          <div className="card-surface p-6 relative overflow-hidden">
            <div className="absolute inset-0 pointer-events-none opacity-60"
              style={{ background: "radial-gradient(circle at 100% 0%, rgba(108,92,231,0.2), transparent 60%)" }} />
            <div className="relative">
              <div className="flex items-center gap-2.5 mb-5">
                <div className="w-9 h-9 rounded-xl bg-white/[0.06] border border-white/10 flex items-center justify-center">
                  <CreditCard className="w-4 h-4 text-white" />
                </div>
                <div>
                  <div className="label-mono">Subscription</div>
                  <h3 className="text-white font-semibold tracking-tight text-[15px]">Plan</h3>
                </div>
              </div>
              <div className="label-mono">Current</div>
              <div className="text-3xl text-white font-semibold tracking-tight mt-1 capitalize" data-testid="profile-plan">{user.plan}</div>
              {user.plan !== "executive" && (
                <Link to="/pricing" className="mt-5 btn-accent inline-flex" data-testid="profile-upgrade-link">Upgrade</Link>
              )}
              <div className="mt-7 pt-5 border-t border-white/5 space-y-2.5 text-xs text-white/45">
                <div className="flex items-center gap-2"><Shield className="w-3 h-3" />Secured by Stripe</div>
                <div className="flex items-center gap-2"><Activity className="w-3 h-3" />Joined {new Date(user.created_at).toLocaleDateString()}</div>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-6">
          <SupportCard />
        </div>
      </main>
      <Footer />
    </div>
  );
}
